import React, { memo, useCallback } from "react";

import { useMutation } from "@apollo/client";
import {
  Button,
  HStack,
  Modal,
  ModalBody,
  ModalContent,
  ModalOverlay,
  Text,
  VStack,
} from "@chakra-ui/react";
import {
  EditMessageThreadCreateMessageUserIdListMutation,
  EditMessageThreadCreateMessageUserIdListMutationVariables,
} from "gql/graphql";
import { ReadOnlyIcon } from "modules/icons";
import EDIT_MESSAGE_THREAD_CREATE_MESSAGE_USER_ID_LIST_MUTATION from "modules/messaging/_networking/edit-message-thread-create-message-user-id-list";
import { useAuth } from "modules/providers/auth-provider";
import { useOverlay } from "modules/providers/overlay-provider";
import colors from "app/colors";
import {
  MIXPANEL_EVENT_TYPE,
  useMixpanel,
} from "../providers/mixpanel-provider/mixpanel-provider";
import ModalTitle from "./modal-title";

interface IProps {
  messageThreadId: string;
}

const ConfirmReadOnlyThreadModal = ({ messageThreadId }: IProps) => {
  const { me } = useAuth();
  const { closeModal } = useOverlay();
  const { track } = useMixpanel();

  const [editReadOnlyAPI] = useMutation<
    EditMessageThreadCreateMessageUserIdListMutation,
    EditMessageThreadCreateMessageUserIdListMutationVariables
  >(EDIT_MESSAGE_THREAD_CREATE_MESSAGE_USER_ID_LIST_MUTATION);

  const onSaveClicked = useCallback(async () => {
    if (!me) return;
    editReadOnlyAPI({
      variables: {
        id: messageThreadId,
        createMessageUserIdList: [me.id],
      },
      optimisticResponse: {
        __typename: "Mutation",
        editMessageThread: {
          __typename: "MessageThread",
          id: messageThreadId,
          createMessageUserIdList: [me.id],
        },
      },
    });
    track(MIXPANEL_EVENT_TYPE.READ_ONLY_CONVERSATION_TOGGLED, {
      "Read-Only Set": true,
      "New Conversation": false,
    });
    closeModal();
  }, [me, editReadOnlyAPI, messageThreadId, track, closeModal]);

  return (
    <Modal isOpen onClose={closeModal} isCentered size="sm">
      <ModalOverlay />
      <ModalContent borderRadius={16}>
        <ModalBody p={6}>
          <VStack spacing={4}>
            <ModalTitle
              text="Make Thread Read-Only"
              icon={<ReadOnlyIcon boxSize={16} />}
            />
            <Text
              textAlign="center"
              fontSize={14}
              fontWeight={500}
              color={colors.gray50}
            >
              Enabling this setting allows only you to post messages to this
              thread. Remove this setting in the thread action menu to allow
              everyone in the thread to post messages.
            </Text>
            <HStack>
              <Button variant="cancel" height={8} w={28} onClick={closeModal}>
                Cancel
              </Button>
              <Button height={8} w={28} onClick={onSaveClicked}>
                Save
              </Button>
            </HStack>
          </VStack>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default memo(ConfirmReadOnlyThreadModal);
